// Helpers de DOM y formato compartidos por las pantallas.

import type { CatalogoItem } from './api-types';
import type { OpcionSelect } from './catalogos';

/**
 * Busca un elemento por id y falla ruidosamente si no esta.
 *
 * Devolver el elemento no-nulo evita el `!` repetido en cada pantalla, y si
 * alguien renombra un id en el Razor el error aparece de una con el id concreto
 * en vez de un "null is not an object" varias lineas despues.
 */
export function byId<T extends HTMLElement = HTMLElement>(id: string): T {
    const el = document.getElementById(id);

    if (!el) {
        throw new Error(`No existe en el DOM el elemento con id "${id}".`);
    }

    return el as T;
}

/** Igual que byId pero devuelve null en vez de fallar, para elementos opcionales. */
export function byIdOpcional<T extends HTMLElement = HTMLElement>(id: string): T | null {
    return document.getElementById(id) as T | null;
}

/**
 * Escapa texto para interpolarlo en HTML.
 *
 * Cualquier valor que venga de la API pasa por aca antes de armar HTML a mano:
 * los comentarios de maquinas y maletines son texto libre del usuario.
 */
export function escaparHtml(valor: unknown): string {
    if (valor === null || valor === undefined) return '';

    return String(valor)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const MESES_CORTOS = [
    'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
    'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
] as const;

/**
 * Formatea una fecha ISO como "06/Ago/2026 11:08 a.m.".
 *
 * Los meses salen de una tabla fija en vez de toLocaleDateString para que el
 * formato no dependa de la configuracion regional del navegador del usuario.
 */
export function formatFecha12h(fechaIso: string | null | undefined): string {
    if (!fechaIso) return '-';

    const d = new Date(fechaIso);
    if (Number.isNaN(d.getTime())) return '-';

    const dia = String(d.getDate()).padStart(2, '0');
    const mes = MESES_CORTOS[d.getMonth()] ?? '???';
    const anio = d.getFullYear();

    const horas24 = d.getHours();
    const horas12 = horas24 % 12 === 0 ? 12 : horas24 % 12;
    const minutos = String(d.getMinutes()).padStart(2, '0');
    const meridiano = horas24 < 12 ? 'a.m.' : 'p.m.';

    return `${dia}/${mes}/${anio} ${String(horas12).padStart(2, '0')}:${minutos} ${meridiano}`;
}

/**
 * Formatea una hora (objeto Date) en 12h, ej. "2:05 PM".
 *
 * Mismo estilo que ya se usa para `horarioSorteo` (sin cero a la izquierda en
 * la hora, AM/PM en mayusculas sin puntos), para que se vea consistente con
 * el resto de la pantalla.
 */
export function formatHora12h(d: Date): string {
    const horas24 = d.getHours();
    const horas12 = horas24 % 12 === 0 ? 12 : horas24 % 12;
    const minutos = String(d.getMinutes()).padStart(2, '0');
    const meridiano = horas24 < 12 ? 'AM' : 'PM';

    return `${horas12}:${minutos} ${meridiano}`;
}

/** Formatea una fecha ISO como "06/Ago/2026", sin hora. */
export function formatFecha(fechaIso: string | null | undefined): string {
    if (!fechaIso) return '-';

    const d = new Date(fechaIso);
    if (Number.isNaN(d.getTime())) return '-';

    const dia = String(d.getDate()).padStart(2, '0');
    const mes = MESES_CORTOS[d.getMonth()] ?? '???';

    return `${dia}/${mes}/${d.getFullYear()}`;
}

/**
 * Llena un `<select>` con un catalogo fijo de `core/catalogos` (valor/texto).
 *
 * Es la variante para catalogos cuyo valor no es numerico: estados como
 * "true"/"false", horarios como "14:00" o identificadores de modulo.
 */
export function poblarSelectOpciones(
    select: HTMLSelectElement,
    opciones: OpcionSelect[],
    textoOpcionPorDefecto?: string
): void {
    poblarSelect(
        select,
        opciones.map((o) => ({ id: o.valor, nombre: o.texto })),
        textoOpcionPorDefecto
    );
}

/**
 * Agrega las opciones de un catalogo id/nombre a un `<select>`.
 *
 * Usa `createElement`/`textContent` en vez de `innerHTML`: no hace falta escapar
 * nada porque el navegador nunca interpreta ese texto como HTML.
 *
 * Si se pasa `textoOpcionPorDefecto`, limpia el select y arma esa opcion antes
 * de los items; si se omite, solo agrega los items a lo que ya haya.
 */
export function poblarSelect(
    select: HTMLSelectElement,
    items: CatalogoItem[] | { id: string | number; nombre: string }[],
    textoOpcionPorDefecto?: string
): void {
    if (textoOpcionPorDefecto !== undefined) {
        select.innerHTML = '';

        const opcionDefecto = document.createElement('option');
        opcionDefecto.value = '';
        opcionDefecto.textContent = textoOpcionPorDefecto;
        select.appendChild(opcionDefecto);
    }

    items.forEach((item) => {
        const opcion = document.createElement('option');
        opcion.value = String(item.id);
        opcion.textContent = item.nombre;
        select.appendChild(opcion);
    });
}
